import React, { useEffect, useState, useCallback } from 'react';
import { paymentMethodsApi, revolutApi, PaymentMethod } from '../services/api';
import { openRevolutCheckout } from '../utils/revolutCheckout';
import { Trans, useTranslation } from 'react-i18next';

// Short label that fits inside the 50px brand chip. Revolut returns brand
// strings like MASTERCARD_DEBIT / VISA_CREDIT — strip the funding suffix
// and shorten well-known networks to a 2-5 char abbreviation.
function shortBrand(raw: string | null | undefined): string {
  const base = (raw || '').toUpperCase().replace(/_(DEBIT|CREDIT|PREPAID)$/i, '');
  const map: Record<string, string> = {
    MASTERCARD: 'MC',
    AMERICAN_EXPRESS: 'AMEX',
    AMEX: 'AMEX',
    DISCOVER: 'DISC',
    VISA: 'VISA',
    JCB: 'JCB',
    UNIONPAY: 'UPI',
    DINERS_CLUB: 'DINERS',
    MAESTRO: 'MAESTRO',
  };
  return map[base] || base || 'CARD';
}

// Full readable brand (for the title text next to the chip). Same as shortBrand
// but doesn't squash MASTERCARD into MC — keeps it human in the title.
function readableBrand(raw: string | null | undefined): string {
  return (raw || '').toUpperCase().replace(/_(DEBIT|CREDIT|PREPAID)$/i, '');
}

interface Props {
  advertiserId: string;
  currency?: string;
}

const PaymentMethodsManager: React.FC<Props> = ({ advertiserId, currency = 'EUR' }) => {
  const { t } = useTranslation();
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await paymentMethodsApi.list(advertiserId);
      setMethods(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('billing.errLoadMethods'));
    } finally {
      setLoading(false);
    }
  }, [advertiserId, t]);

  useEffect(() => { load(); }, [load]);

  const handleAddCard = async () => {
    setBusy('add');
    setError(null);
    try {
      const returnUrl = `${window.location.origin}/billing?card=saved`;
      const resp = await revolutApi.setupCard({
        advertiser_id: advertiserId,
        return_url: returnUrl,
      });
      if (resp.revolut_public_id) {
        await openRevolutCheckout({
          token: resp.revolut_public_id,
          environment: resp.revolut_environment === 'sandbox' ? 'sandbox' : 'production',
          // This is a pure save-card flow — always tokenise for merchant so
          // future auto-topup MIT charges work. Backend refunds the 1-cent.
          savePaymentMethodFor: 'merchant',
          onSuccess: () => {
            // Webhook saves the PM token + refunds the 1-cent auth.
            // Poll-refresh the list a couple of times to pick it up.
            setTimeout(() => load(), 4000);
            setTimeout(() => load(), 10000);
          },
          onError: (msg) => setError(msg),
        });
      } else {
        // Fallback: same-tab redirect
        window.location.assign(resp.setup_url);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('billing.errStartCardSetup'));
    } finally {
      setBusy(null);
    }
  };

  const handleSetDefault = async (id: number) => {
    setBusy(`default-${id}`);
    try {
      await paymentMethodsApi.setDefault(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('billing.errSetDefault'));
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async (id: number) => {
    if (!window.confirm(t('billing.confirmRemoveMethod'))) return;
    setBusy(`remove-${id}`);
    try {
      await paymentMethodsApi.remove(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('billing.errRemoveMethod'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="payment-methods">
      <div className="payment-methods-header">
        <h3>{t('billing.paymentMethods')}</h3>
        <button className="btn-primary" onClick={handleAddCard} disabled={busy === 'add'}>
          {t(busy === 'add' ? 'billing.openingDots' : 'billing.addCard')}
        </button>
      </div>

      {error && <div className="alert-error">{error}</div>}

      {loading ? (
        <div className="loading">{t('billing.loadingDots')}</div>
      ) : methods.length === 0 ? (
        <div className="empty-state">
          <p>{t('billing.noSavedMethods')}</p>
          <p className="muted">{t('billing.addCardHint')}</p>
        </div>
      ) : (
        <ul className="payment-methods-list">
          {methods.map(m => {
            const isExpired = m.status === 'expired';
            const noMit = (m.metadata as any)?.mit_supported === false;
            const mismatch = !!m.profile_mismatch;
            // Revolut sometimes returns "MASTERCARD_DEBIT" / "VISA_CREDIT" —
            // strip the funding-type suffix for display.
            return (
              <li key={m.id} className={`payment-method-row ${m.is_default_for_topup ? 'default' : ''} ${isExpired ? 'expired' : ''} ${noMit ? 'no-mit' : ''} ${mismatch ? 'mismatch' : ''}`}>
                <div className="pm-icon">{shortBrand(m.brand)}</div>
                <div className="pm-label">
                  <div className="pm-title">
                    {`${readableBrand(m.brand)} •••• ${m.last4 || ''}`.trim() || m.method_type}
                  </div>
                  <div className="pm-meta-row">
                    {m.is_default_for_topup && !isExpired && !noMit && !mismatch && <span className="pm-badge">{t('billing.autoTopup')}</span>}
                    {isExpired && <span className="pm-badge expired">{t('billing.cardExpired')}</span>}
                    {noMit && <span className="pm-badge sandbox">{t('billing.badgeSandbox')}</span>}
                    {mismatch && <span className="pm-badge mismatch" title={t('billing.profileChangedTitle')}>{t('billing.badgeProfileChanged')}</span>}
                    {m.payment_profile_name && (
                      <span className="pm-badge profile" title={t('billing.issuedUnderProfile', { id: m.payment_profile_id })}>
                        {m.payment_profile_name}
                      </span>
                    )}
                  </div>
                </div>
                <div className="pm-actions">
                  {!m.is_default_for_topup && !isExpired && !noMit && !mismatch && (
                    <button
                      className="btn-link"
                      onClick={() => handleSetDefault(m.id)}
                      disabled={busy === `default-${m.id}`}
                    >
                      {t('billing.useForAutoTopup')}
                    </button>
                  )}
                  <button
                    className="btn-link danger"
                    onClick={() => handleRemove(m.id)}
                    disabled={busy === `remove-${m.id}`}
                  >
                    {t('clients.remove')}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

    </div>
  );
};

export default PaymentMethodsManager;
